/**
 * Org-scoped News Feed (CVA-4718) — Service layer
 *
 * Thin wrapper so the shared feed's data-access file has the same shape as
 * the Tenant feed's `adminNewsFeedService` (fetch/create/edit/delete/like).
 * No new backend calls are introduced — every function here delegates to
 * the existing org-scoped `/v2/post/*` endpoints already used by
 * `services/post/index.tsx` (`JwtAuthGuard` only, org derived from the JWT).
 */
import {
  ICreatePost,
  IPostQuery,
  IPostListResponse,
  IPostCreateResponse,
  IPostEditResponse,
  IPostDeleteResponseBody,
} from '../../interface/postModel';
import api from '../api';
import { getPost, postCreate, postEdit, deletePost } from '../post';

/** Fetches paginated, newest-first posts scoped to the caller's organization. */
export const fetchNewsFeedPosts = (query: IPostQuery): Promise<IPostListResponse> => getPost(query);

/** Creates a post — mirrors `postCreate` (`POST /post/progress`, multipart). */
export const createNewsFeedPost = (payload: ICreatePost): Promise<IPostCreateResponse> =>
  postCreate(payload);

/** Edits a post — mirrors `postEdit` (`PUT /post/:id`, multipart). */
export const editNewsFeedPost = (payload: FormData): Promise<IPostEditResponse> =>
  postEdit(payload);

/** Deletes a post — mirrors `deletePost` (`DELETE /post/:id`). */
export const deleteNewsFeedPost = (id: string): Promise<IPostDeleteResponseBody> => deletePost(id);

/**
 * Toggles like state for a post (`POST /post/like`). Like state is managed
 * optimistically by the caller, so this resolves with no payload — same
 * pattern as the existing `toggleLike` thunk in `redux/action/post.action.ts`.
 */
export const toggleNewsFeedPostLike = async (postId: string): Promise<void> => {
  await api.post(`/post/like`, { post_template_id: postId });
};
